import React, { useCallback, useMemo, useState } from 'react'
import { useNewTranslation } from 'hooks/index'
import * as yup from 'yup'
import { Form, Formik, FormikProps } from 'formik'
import { useDispatch, useSelector } from 'react-redux'
import { SUBMISSION_TASK_STATUS_ENUM, SUBMISSION_VIEW_ENUM, updateCompletedSchedule } from 'state/submissions'
import {
    selectSelectedSubmissionView,
    makeShowPostTimeSelector,
    SubmissionProgressProps,
    selectSelectedCampaignSummary,
} from 'state/review'
import { State } from 'state'
import { convertToBackendStr, parseBackendDate } from 'services/dates'
import { selectCanEditSchedulePostTime } from 'state/profile'
import { StepState, TaskState } from 'state/tasks'
import { POST_TIME_WINDOW_SCHEMA } from '../../validationSchema'
import RangeInput from 'particles/RangeInput'
import { PostTimeContainer } from './styles'
import RectangleButton from 'particles/Button/RectangleButton'
import Text from 'particles/Text'

interface PostTimeProps {
    submissionProgress: SubmissionProgressProps
    task: TaskState
    step: StepState
}

interface FormikValues {
    data: {
        post_time_window?: {
            start: string
            end: string
        } | null
    }
}

const PostTime: React.FC<PostTimeProps> = ({ submissionProgress, task, step }) => {
    const [newT] = useNewTranslation()
    const dispatch = useDispatch()
    const selectShowPostTime = useMemo(makeShowPostTimeSelector, [])
    const showPostTime = useSelector((state: State) => selectShowPostTime(state, { submissionProgress, task, step }))
    const selectedSubmissionView = useSelector(selectSelectedSubmissionView)
    const campaignSummary = useSelector(selectSelectedCampaignSummary)
    const selectedCampaignUuid = campaignSummary?.uuid
    const canEditSchedulePostTime = useSelector(selectCanEditSchedulePostTime)
    const [isEditMode, setIsEditMode] = useState<boolean>(false)

    const handleUpdateCompletedSchedule = useCallback(
        (data: any) => {
            if (selectedCampaignUuid) {
                dispatch(
                    updateCompletedSchedule({
                        submissionUuid: submissionProgress.uuid,
                        data,
                    }),
                )
            }
        },
        [selectedCampaignUuid, submissionProgress],
    )
    if (!showPostTime || !campaignSummary) {
        return null
    }
    const postTimeWindow =
        selectedSubmissionView === SUBMISSION_VIEW_ENUM.FOR_REVIEW
            ? submissionProgress &&
              submissionProgress.pending_action &&
              submissionProgress.pending_action.data &&
              submissionProgress.pending_action.data.data &&
              submissionProgress.pending_action.data.data['post_time_window'] != null &&
              submissionProgress.pending_action.data.data['post_time_window'].start != null &&
              submissionProgress.pending_action.data.data['post_time_window'].end != null &&
              submissionProgress.pending_action.data.data['post_time_window'].start.length > 0 &&
              submissionProgress.pending_action.data.data['post_time_window'].end.length > 0
                ? {
                      start: parseBackendDate(
                          submissionProgress.pending_action.data.data['post_time_window'].start,
                          campaignSummary.country,
                          {
                              showTime: true,
                          },
                      ),
                      end: parseBackendDate(
                          submissionProgress.pending_action.data.data['post_time_window'].end,
                          campaignSummary.country,
                          {
                              showTime: true,
                          },
                      ),
                  }
                : null
            : submissionProgress &&
                submissionProgress.submission_task &&
                submissionProgress.submission_task.post_time_window
              ? {
                    start: parseBackendDate(
                        submissionProgress.submission_task.post_time_window.start,
                        campaignSummary.country,
                        {
                            showTime: true,
                        },
                    ),
                    end: parseBackendDate(
                        submissionProgress.submission_task.post_time_window.end,
                        campaignSummary.country,
                        { showTime: true },
                    ),
                }
              : null

    const onDateChange = (handleChange: any) => (date: Date | null) => {
        handleChange(date ? parseBackendDate(date.toISOString(), undefined, { showTime: true }) : null)
    }

    const validationSchema = yup.object({
        data: yup.object({
            post_time_window: POST_TIME_WINDOW_SCHEMA(task, campaignSummary.country),
        }),
    })

    return (
        <PostTimeContainer>
            <Formik
                initialValues={{
                    data: {
                        post_time_window: postTimeWindow,
                    },
                }}
                enableReinitialize={true}
                validationSchema={validationSchema}
                onSubmit={(values: { data: any }, actions): void => {
                    // Cloning the values prevents displaying the backend format string in the date input after submission.
                    const dataCopy = { ...values.data }
                    if (campaignSummary) {
                        dataCopy.post_time_window = {
                            start: convertToBackendStr(dataCopy.post_time_window.start, campaignSummary.country),
                            end: convertToBackendStr(dataCopy.post_time_window.end, campaignSummary.country),
                        }
                        handleUpdateCompletedSchedule(dataCopy)
                    }
                    actions.setSubmitting(false)
                    setIsEditMode(false)
                }}
            >
                {(formikBag: FormikProps<FormikValues>): JSX.Element => {
                    return (
                        <Form>
                            <div className="schedule-header">
                                <Text scale="bodyS500" textAlign="left">
                                    {newT('submission.review.schedulePostHeader', 'Schedule Post On')}
                                </Text>
                                {selectedSubmissionView === SUBMISSION_VIEW_ENUM.COMPLETED && (
                                    <div style={{ textAlign: 'right', display: 'flex' }}>
                                        {canEditSchedulePostTime && !isEditMode ? (
                                            (submissionProgress?.submission_task?.status ===
                                                SUBMISSION_TASK_STATUS_ENUM.ACTION_REQUIRED ||
                                                submissionProgress?.submission_task?.status ===
                                                    SUBMISSION_TASK_STATUS_ENUM.EDITS_REQUESTED) && (
                                                <RectangleButton
                                                    label={newT('button.edit')}
                                                    form="text"
                                                    color="neutral"
                                                    onClick={e => {
                                                        e.preventDefault()
                                                        setIsEditMode(true)
                                                    }}
                                                    className="action-button"
                                                />
                                            )
                                        ) : isEditMode ? (
                                            <>
                                                <RectangleButton
                                                    label={newT('button.cancel')}
                                                    form="text"
                                                    color="neutral"
                                                    onClick={(): void => {
                                                        setIsEditMode(false)
                                                    }}
                                                    className="action-button"
                                                />
                                                <RectangleButton
                                                    label={newT('button.save')}
                                                    form="text"
                                                    color="primary"
                                                    size="s"
                                                    type="submit"
                                                    onClick={(): void => {
                                                        setIsEditMode(false)
                                                        formikBag.submitForm()
                                                    }}
                                                    className="action-button"
                                                />
                                            </>
                                        ) : null}
                                    </div>
                                )}
                            </div>
                            <RangeInput.Date
                                firstRangePlaceholder={newT('general.information.startDate', 'Start Date')}
                                firstRangeName="data.post_time_window.start"
                                secondRangeName="data.post_time_window.end"
                                secondRangePlaceholder={newT('general.information.endDate', 'End Date')}
                                firstRangeOnChange={onDateChange(formikBag.handleChange('data.post_time_window.start'))}
                                secondRangeOnChange={onDateChange(formikBag.handleChange('data.post_time_window.end'))}
                                firstRangeDate={
                                    formikBag.values.data.post_time_window
                                        ? formikBag.values.data.post_time_window.start
                                        : undefined
                                }
                                secondRangeDate={
                                    formikBag.values.data.post_time_window
                                        ? formikBag.values.data.post_time_window.end
                                        : undefined
                                }
                                size="s"
                                onClickClear={name => name && formikBag.setFieldValue(name, '')}
                                errorMessage={
                                    (formikBag.errors.data as FormikValues['data'])?.post_time_window?.start ||
                                    (formikBag.errors.data as FormikValues['data'])?.post_time_window?.end
                                }
                                showTime
                                inputWidth="100%"
                                firstRangePopperPlacement="bottom-start"
                                secondRangePopperPlacement="bottom-start"
                                isReadOnly={!isEditMode}
                                isVertical
                                className="date-range-input-container"
                            />
                        </Form>
                    )
                }}
            </Formik>
        </PostTimeContainer>
    )
}

export default PostTime
